/* eslint-disable jsx-a11y/anchor-is-valid */

import { Body } from '@hover/blueprint';
import { dArrowRight, Link } from '@hover/blueprint/brand';
import {
  chakra,
  LinkBoxProps,
  LinkOverlay as ChakraLinkOverlay,
  LinkOverlayProps as ChakraLinkOverlayProps,
} from '@hover/blueprint/chakra';
import { Link as GatsbyLink } from 'gatsby';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { StyledDescription, StyledLinkBox } from './helperComponents';

interface ICardProps {
  id: number;
  description: string;
  title: string;
  responsiveLinkTitle?: string;
  linkTitle: string;
  linkTo: string;
  nativeLink?: boolean;
  image: React.ReactNode;
  titleColor?: string;
  bgColor?: string;
  mobileImage: React.ReactNode;
}

export interface ILearningCardProps extends LinkBoxProps {
  card: ICardProps;
  cardIndex: number;
}

type LinkOverlayComponent = FunctionComponent<ChakraLinkOverlayProps>;

const LearningCard: React.FC<ILearningCardProps> = ({
  card,
  cardIndex,
  ...boxProps
}) => {
  const to = card.linkTo;

  const NativeLinkOverlay: LinkOverlayComponent = useCallback(
    props => <ChakraLinkOverlay {...props} href={to} />,
    [to],
  );

  const GatsbyLinkOverlay: LinkOverlayComponent = useCallback(
    props => <ChakraLinkOverlay {...props} to={to} as={GatsbyLink} />,
    [to],
  );

  const LinkOverlay = card.nativeLink ? NativeLinkOverlay : GatsbyLinkOverlay;

  const [isOver, setIsOver] = useState(false);

  return (
    <StyledLinkBox
      {...boxProps}
      bgColor={card.bgColor}
      borderTopRadius={500}
      borderBottomRadius={{ base: 200, desktop: 500 }}
      onMouseEnter={() => setIsOver(true)}
      onMouseLeave={() => setIsOver(false)}
    >
      <chakra.div
        d={{ base: 'none', tablet: 'flex' }}
        pointerEvents="none"
        borderRadius={8}
        overflow="hidden"
        h="100%"
      >
        {card.image}
      </chakra.div>
      <chakra.div d={{ base: 'flex', tablet: 'none' }} pointerEvents="none">
        {card.mobileImage}
      </chakra.div>
      <StyledDescription px={{ base: 300, mobile: 400, tablet: 200 }} pb={500}>
        <Body
          as="h3"
          mt={600}
          letterSpacing="0.5px"
          color={card.titleColor}
          isBold
          size={300}
          textTransform="uppercase"
        >
          <LinkOverlay>{card.title}</LinkOverlay>
        </Body>
        <Body
          mt={400}
          pb={{ desktop: 100 }}
          size={{ base: 500, desktop: 400 }}
          mb={{ base: 500, desktop: 0 }}
        >
          {card.description}
        </Body>
        <Link
          as={GatsbyLink}
          to={to}
          mt={{ base: 'auto', tablet: 'inherit', desktop: 'auto' }}
          d="flex"
          alignItems="center"
          justifyContent="flex-start"
          decorationAfter={dArrowRight}
          isOver={isOver}
          className="link-design"
        >
          <Body
            as="span"
            d={{ base: 'flex', tablet: 'none' }}
            color="primary.600"
            isSemiBold
            whiteSpace="nowrap"
            mr={200}
            size={500}
          >
            {card.responsiveLinkTitle ?? card.linkTitle}
          </Body>
          <Body
            as="span"
            whiteSpace="nowrap"
            id="better-for-everyone-card-title"
            d={{ base: 'none', tablet: 'flex' }}
            color="primary.600"
            isSemiBold
            mr={100}
            size={500}
          >
            {card.linkTitle}
          </Body>
        </Link>
      </StyledDescription>
    </StyledLinkBox>
  );
};

export default LearningCard;
